import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, UserPlus, X } from "lucide-react";

/**
 * Customer selector with search over name/phone/instagram.
 * Props: onSelect(customer) — called when a customer row is picked
 *        onManual() — called when user chooses "Enter New Customer Manually"
 */
export default function CustomerPicker({ onSelect, onManual }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return customers.slice(0, 8);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.phone || "").toLowerCase().includes(term) ||
      (c.instagram || "").toLowerCase().includes(term),
    ).slice(0, 12);
  }, [customers, q]);

  return (
    <div className="relative" ref={wrapRef}>
      <Label className="text-xs text-[#4B5563]">Repeat Customer — search by name, phone or Instagram</Label>
      <div className="relative mt-1">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[#4B5563]" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Start typing name, phone or @handle…"
          className="h-8 pl-8 border-[#E8E3DF]"
          data-testid="customer-search-input"
        />
        {q && (
          <button type="button" onClick={() => setQ("")} className="absolute right-2 top-2 text-[#4B5563]" data-testid="customer-search-clear">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E8E3DF] rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="customer-search-results">
          <button
            type="button"
            onClick={() => { setOpen(false); onManual?.(); }}
            className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#FCF9F9] border-b border-[#E8E3DF] text-sm text-[#B86F77] font-medium"
            data-testid="customer-manual-entry-btn"
          >
            <UserPlus className="h-4 w-4" /> Enter New Customer Manually
          </button>
          {results.length === 0 && (
            <div className="px-3 py-4 text-xs text-[#4B5563] text-center">
              {q ? "No matches. Choose 'Enter New Customer Manually' above." : "No customers yet."}
            </div>
          )}
          {results.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => { setOpen(false); setQ(""); onSelect?.(c); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F3E8E9] border-b border-[#E8E3DF]/60 last:border-b-0"
              data-testid={`customer-picker-row-${c.id}`}
            >
              <div className="text-sm font-medium text-[#141E30]">{c.name}</div>
              <div className="text-[11px] text-[#4B5563]">
                {c.phone}{c.instagram ? ` · @${c.instagram.replace(/^@/, "")}` : ""}{c.email ? ` · ${c.email}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
